/**
 * injuryEntries — turns the injury report + the ticker's fixtures into the
 * cards the injury ticker cycles through.
 *
 * Entries are grouped by league (same order as the game ticker) so the
 * "‹ NBA / NFL ›" labels and the skip targets come from one section list.
 * Inside an entry, players are grouped by team (away first) and ordered most
 * severe first.
 */
import { sortByLeague, leagueKey } from './tickerNavigation';
import { passesSeverity, severityRank } from './injuryStatus';

export const MAX_PLAYERS_PER_ENTRY = 12;

const normalizeTeam = (name) => {
    if (!name) return '';
    return name.toLowerCase().replace(/^(the|los|las|new|san|st\.?|saint)\s+/i, '').replace(/[^a-z0-9\s]/g, '').trim();
};

export const teamMatches = (injuryTeam, fixtureTeam) => {
    if (!injuryTeam || !fixtureTeam) return false;
    const a = normalizeTeam(injuryTeam);
    const b = normalizeTeam(fixtureTeam);
    if (!a || !b) return false;
    if (a === b) return true;
    if (a.length > 3 && b.includes(a)) return true;
    if (b.length > 3 && a.includes(b)) return true;
    const aLast = a.split(/\s+/).pop();
    const bLast = b.split(/\s+/).pop();
    if (aLast.length > 3 && aLast === bLast) return true;
    return false;
};

export function fixtureStartMs(startDate) {
    if (!startDate) return null;
    let d;
    if (startDate._seconds) d = new Date(startDate._seconds * 1000);
    else if (startDate.seconds) d = new Date(startDate.seconds * 1000);
    else d = new Date(startDate);
    const ms = d.getTime();
    return Number.isFinite(ms) ? ms : null;
}

const leagueMatches = (playerLeague, fixtureLeague) => {
    const pl = leagueKey(playerLeague);
    if (!pl || !fixtureLeague) return true;
    return pl.includes(fixtureLeague) || fixtureLeague.includes(pl);
};

const bySeverityThenName = (a, b) => {
    const r = severityRank(a.injury_status) - severityRank(b.injury_status);
    return r !== 0 ? r : String(a.name || '').localeCompare(String(b.name || ''));
};

/** Most severe first, capped, then split into per-team groups in `teamOrder`. */
function groupPlayers(matched, teamOrder) {
    const shown = [...matched].sort(bySeverityThenName).slice(0, MAX_PLAYERS_PER_ENTRY);
    const placed = new Set(); // a fuzzy name match must not list a player under both teams
    const groups = teamOrder.map((team) => {
        const players = shown.filter((p) => !placed.has(p) && teamMatches(p.team, team));
        players.forEach((p) => placed.add(p));
        return { team, players };
    }).filter((g) => g.players.length > 0);
    return { groups, shownCount: shown.length };
}

/**
 * @param {object} args
 * @param {Array} args.fixtures   ticker fixtures
 * @param {Array} args.injuries   getInjuryReport players
 * @param {string[]|null} args.leagues  injury ticker league filter (null/[] = all)
 * @param {string} args.minSeverity     'all' | 'questionable_up' | 'doubtful_up' | 'out_only'
 * @param {'fixture'|'team'} args.groupBy
 */
export function buildInjuryEntries({ fixtures, injuries, leagues, minSeverity, groupBy }) {
    if (!fixtures?.length || !injuries?.length) return [];
    const allowed = leagues && leagues.length > 0 ? new Set(leagues.map(leagueKey)) : null;
    const isTeamMode = groupBy === 'team';
    const entries = [];
    const seen = new Set();

    for (const fx of fixtures) {
        const home = fx.home_team || '';
        const away = fx.away_team || '';
        const league = leagueKey(fx.league || fx.sport);
        if (allowed && !allowed.has(league)) continue;
        const fixtureId = String(fx.fixture_id || fx.id || `${away}@${home}`);
        if (seen.has(fixtureId)) continue;
        seen.add(fixtureId);
        const startMs = fixtureStartMs(fx.start_date);
        const pool = injuries.filter((p) => leagueMatches(p.league, league) && passesSeverity(p.injury_status, minSeverity));

        if (isTeamMode) {
            for (const team of [away, home]) {
                const matched = pool.filter((p) => teamMatches(p.team, team));
                if (matched.length === 0) continue;
                const { groups, shownCount } = groupPlayers(matched, [team]);
                entries.push({
                    id: `${fixtureId}_${team}`,
                    teamMode: true,
                    team,
                    opponent: team === away ? home : away,
                    isHome: team === home,
                    home_team: home,
                    away_team: away,
                    league,
                    startMs,
                    groups,
                    shownCount,
                    total: matched.length,
                });
            }
        } else {
            const matched = pool.filter((p) => teamMatches(p.team, home) || teamMatches(p.team, away));
            if (matched.length === 0) continue;
            const { groups, shownCount } = groupPlayers(matched, [away, home]);
            entries.push({
                id: fixtureId,
                teamMode: false,
                home_team: home,
                away_team: away,
                league,
                startMs,
                groups,
                shownCount,
                total: matched.length,
            });
        }
    }
    return sortByLeague(entries, (e) => e.league, (a, b) => (a.startMs ?? Infinity) - (b.startMs ?? Infinity));
}

/** Short team name for the in-chip team tag: "Buffalo Bills" → "Bills". */
export function shortTeamName(name) {
    const words = String(name || '').trim().split(/\s+/).filter(Boolean);
    if (words.length < 2) return words[0] || '';
    const lastTwo = words.slice(-2).join(' ').toLowerCase();
    if (['trail blazers', 'red sox', 'white sox', 'blue jays', 'golden knights', 'maple leafs', 'red wings', 'blue jackets'].includes(lastTwo)) {
        return words.slice(-2).join(' ');
    }
    return words[words.length - 1];
}
